import React, { useState } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { Dashboard } from './pages/Dashboard';
import { Login } from './components/Login';
import { Register } from './components/Register';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Anogi Traders React Error Boundary caught an error:', error, errorInfo);
  }

  handleReset = () => {
    localStorage.clear();
    window.location.reload();
  };

  handleAutoFix = async () => {
    try {
      localStorage.clear();
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: 'invest@anogilimited.com', password: 'Zeleninova30' })
      });
      const data = await res.json();
      if (data.token && data.user) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
      }
    } catch (e) {
      console.error('Auto fix login failed', e);
    }
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-900 text-white p-6 text-center">
          <div className="max-w-lg w-full glass-panel rounded-3xl p-8 bg-slate-800 border border-slate-700 shadow-2xl">
            <h2 className="text-2xl font-bold text-rose-400 mb-2">Anogi Traders Recovery</h2>
            <p className="text-slate-300 text-sm mb-4">
              {this.state.error?.message || 'A client-side initialization error occurred.'}
            </p>
            <div className="space-y-3">
              <button
                onClick={this.handleAutoFix}
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-3.5 rounded-xl transition-all shadow-lg flex items-center justify-center gap-2"
              >
                <span>⚡</span>
                <span>Auto-Fix & Launch Admin Dashboard</span>
              </button>
              <button
                onClick={this.handleReset}
                className="w-full bg-slate-700 hover:bg-slate-600 text-slate-200 font-semibold py-3 rounded-xl transition-all"
              >
                Clear Cache & Reset Session
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const AppContent: React.FC = () => {
  const { user } = useAuth();
  const [isRegistering, setIsRegistering] = useState(false);

  if (!user) {
    if (isRegistering) {
      return <Register onSwitch={() => setIsRegistering(false)} />;
    }
    return <Login onSwitch={() => setIsRegistering(true)} />;
  }

  return <Dashboard />;
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <LanguageProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </LanguageProvider>
    </ErrorBoundary>
  );
};

export default App;
